import { saveBlobAsFile } from '@/api/apiUtils'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { ComplianceDeleteDialog, ComplianceMasterForm } from '.'
import {
    createComplianceEntry,
    deleteComplianceEntries,
    downloadComplianceExcel,
    getComplianceCategoryList,
    getComplianceEntryList,
    updateComplianceEntry,
} from '../../api/ComplianceMasterApis'
import { getEntityList } from '../../api/GetEntityMasterApi'
import { getTANList } from '../../api/adminTAN'
import DeleteIcon from '../../assets/icons/DeleteIcon'
import DownloadIcon from '../../assets/icons/DownloadIcon'
import SearchIconBtn from '../../assets/icons/SearchIconBtn'
import {
    columns,
    searchFields,
} from '../../constants/complianceMasterColumn.jsx'
import { GLOBAL_CONSTANTS } from '../../constants/globalConstants.js'
import { DEFAULT_PAGE, getValidCriteria } from '../../constants/index.js'
import { useSnackbar } from '../../contexts/SnackbarProvider'
import {
    getFinancialYearForDate,
    syncEntityNamesAndTans,
} from '../../utils/complianceMasterUtils'
import { CommonModal, CommonTable, Search } from '../common'

// The categories API returns an array of strings (e.g. ['a', 'ab']). The form
// expects objects with a `name`, so normalize strings into { name } shape.
const normalizeCategories = (data = []) =>
  (Array.isArray(data) ? data : []).map(item =>
    typeof item === 'string' ? { name: item } : item
  )

// Convert a 'DD/MM/YYYY' string to the API's 'YYYY-MM-DD' start date.
const toApiDate = ddmmyyyy => {
  if (!ddmmyyyy) return ''
  const [day, month, year] = ddmmyyyy.split('/')
  if (!day || !month || !year) return ''
  return `${year}-${month}-${day}`
}

// Convert an ISO API date ("2026-01-31") to 'DD/MM/YYYY'.
const fromApiDate = value => {
  if (!value) return ''
  const parsed = dayjs(String(value))
  if (!parsed.isValid()) return String(value)
  return parsed.format('DD/MM/YYYY')
}

// Normalize an API list row: alias uuid for row selection and format the date
// fields for display. Field names are kept exactly as the API returns them
// (tanName, complianceDueDate, complianceStatus, complianceCompletionDate,
// complianceStartDate) so the table columns bind directly.
const mapComplianceRow = entry => ({
  ...entry,
  uuid: entry.uuid || entry.id,
  complianceDueDate: fromApiDate(entry.complianceDueDate),
})

const DEFAULT_FORM_VALUES = {
  entityNames: [],
  entityTANs: [],
  complianceCategories: [],
  compliancePeriod: '',
  compliancePeriodDate: '',
  dueDate: '',
  status: '',
  completionDate: '',
}

const ComplianceMaster = ({ roleConfig = { canEdit: true } }) => {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)

  const [entities, setEntities] = useState([])
  const [tanList, setTanList] = useState([])
  const [categories, setCategories] = useState([])

  const [modalOpen, setModalOpen] = useState(false)
  const [formMode, setFormMode] = useState('add')
  const [editingEntryId, setEditingEntryId] = useState(null)
  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES)
  const [formErrors, setFormErrors] = useState({})
  const [formLoading, setFormLoading] = useState(false)

  const [selectedItems, setSelectedItems] = useState([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTargets, setDeleteTargets] = useState([])

  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)

  const { showSnackbar } = useSnackbar()

  const hasLoadedData = useRef(false)

  const loadAllData = async (
    page = 1,
    pageSize = itemsPerPage,
    searchList = []
  ) => {
    setLoading(true)
    try {
      const [
        entriesResponse,
        categoriesResponse,
        entitiesResponse,
        tanResponse,
      ] = await Promise.all([
        getComplianceEntryList({
          pageFilter: {
            currentPage: page,
            includeCount: true,
            pageSize: pageSize,
            sortField: 'createdDate',
            sortOrder: 'DESC',
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: searchList,
        }),
        getComplianceCategoryList(),
        getEntityList({
          pageFilter: {
            currentPage: 1,
            includeCount: true,
            pageSize: DEFAULT_PAGE,
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: [],
        }),
        getTANList({
          pageFilter: {
            currentPage: 1,
            includeCount: true,
            pageSize: DEFAULT_PAGE,
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: [],
        }),
      ])

      if (entriesResponse.success) {
        // CommonTable's checkbox/row-selection logic keys off `row.uuid`; ensure
        // each entry carries a `uuid` so per-row selection/edit works.
        const rawContent =
          entriesResponse.data?.data?.content ||
          entriesResponse.data?.content ||
          entriesResponse.data?.data ||
          entriesResponse.data ||
          []
        const content = (Array.isArray(rawContent) ? rawContent : []).map(
          mapComplianceRow
        )
        setEntries(content)
        setTotalItems(
          entriesResponse.data?.data?.totalElements ||
            entriesResponse.data?.totalElements ||
            content.length ||
            0
        )
        setCurrentPage(page)
      } else {
        showSnackbar(entriesResponse.error, 'error')
      }

      if (categoriesResponse.success) {
        setCategories(normalizeCategories(categoriesResponse.data))
      }

      // Process entities (same source/shape as the Assign Entity form)
      if (entitiesResponse.success && entitiesResponse.data?.data?.content) {
        const entitiesData = entitiesResponse.data.data.content.map(entity => ({
          id: entity.uuid || entity.id,
          uuid: entity.uuid,
          name: entity.entityName,
        }))
        setEntities(entitiesData)
      } else {
        showSnackbar(entitiesResponse.error, 'error')
      }

      // Process TANs with entity associations (same source/shape as Assign Entity)
      if (tanResponse?.content) {
        const tanData = tanResponse.content.map(tanEntity => ({
          tan: tanEntity.taxDeductionAccountNumber,
          entityName: tanEntity.entityName,
          entityUuid: tanEntity.uuid,
          ...tanEntity,
        }))
        setTanList(tanData)
      } else {
        showSnackbar(tanResponse?.error, 'error')
      }
    } catch (error) {
      console.error('Error loading Compliance Master data:')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (hasLoadedData.current) return
    hasLoadedData.current = true
    loadAllData()
  }, [])

  const validateForm = () => {
    const errors = {}
    if (!formValues.entityNames || formValues.entityNames.length === 0) {
      errors.entityNames = 'At least one Entity Name is required'
    }
    if (!formValues.entityTANs || formValues.entityTANs.length === 0) {
      errors.entityTANs = 'At least one Entity TAN is required'
    }
    if (
      !formValues.complianceCategories ||
      formValues.complianceCategories.length === 0
    ) {
      errors.complianceCategories =
        'At least one Compliance Category is required'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleAddClick = async () => {
    const today = dayjs()
    setFormMode('add')
    setEditingEntryId(null)
    setFormValues({
      ...DEFAULT_FORM_VALUES,
      compliancePeriod: getFinancialYearForDate(today.toDate()),
      compliancePeriodDate: today.format('DD/MM/YYYY'),
    })
    setFormErrors({})
    setModalOpen(true)

    // Fetch the Compliance Category options fresh each time the Add modal opens.
    const categoriesResponse = await getComplianceCategoryList()
    if (categoriesResponse.success) {
      setCategories(normalizeCategories(categoriesResponse.data))
    } else {
      showSnackbar(categoriesResponse.error, 'error')
    }
  }

  const handleEditClick = row => {
    setFormMode('edit')
    setEditingEntryId(row.uuid || row.id)
    setFormValues({
      entityNames: row.entityName ? [row.entityName] : [],
      entityTANs: row.tanName ? [row.tanName] : [],
      complianceCategories: row.complianceCategory
        ? [row.complianceCategory]
        : [],
      compliancePeriod: row.compliancePeriod || '',
      compliancePeriodDate: fromApiDate(row.complianceStartDate),
      dueDate: row.complianceDueDate || '',
      status: row.complianceStatus || '',
      completionDate: row.complianceCompletionDate || '',
    })
    setFormErrors({})
    setModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setFormErrors({})
  }

  const handleFieldChange = (field, value) => {
    setFormValues(prev => {
      if (field === 'entityNames' || field === 'entityTANs') {
        const synced = syncEntityNamesAndTans({
          entityNames: field === 'entityNames' ? value : prev.entityNames,
          entityTANs: field === 'entityTANs' ? value : prev.entityTANs,
          changedField: field,
          tanLookup: tanList,
        })
        return { ...prev, ...synced }
      }

      return { ...prev, [field]: value }
    })

    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleFormSubmit = async e => {
    e.preventDefault()

    if (formMode === 'edit') {
      // Only the Completion Date is editable in edit mode; persist it and
      // reflect the change in the table.
      setFormLoading(true)
      try {
        const response = await updateComplianceEntry(editingEntryId, {
          completionDate: toApiDate(formValues.completionDate),
        })
        if (response.success) {
          setModalOpen(false)
          setEditingEntryId(null)
          showSnackbar('Compliance updated successfully', 'success')
          // Refresh the table from the backend to reflect the updated entry.
          await loadAllData(
            currentPage,
            itemsPerPage,
            getValidCriteria(searchCriteria)
          )
        } else {
          showSnackbar(response.error, 'error')
        }
      } catch (error) {
        showSnackbar(
          error.message || 'Failed to update Compliance entry',
          'error'
        )
      } finally {
        setFormLoading(false)
      }
      return
    }

    const isValid = validateForm()
    if (!isValid) return

    setFormLoading(true)
    try {
      // Resolve selected entity names → entityUuids and selected TANs → tanUuids.
      const entityUuids = [
        ...new Set(
          formValues.entityNames
            .map(name => entities.find(e => e.name === name)?.uuid)
            .filter(Boolean)
        ),
      ]
      const tanUuids = [
        ...new Set(
          formValues.entityTANs
            .map(tan => tanList.find(item => item.tan === tan)?.uuid)
            .filter(Boolean)
        ),
      ]

      const payload = {
        // India screen — the shared create API is country-scoped.
        countryCode: GLOBAL_CONSTANTS.INDIA_COUNTRY_CODE_COMPLIANCE,
        countryName:'India',
        complianceCategories: formValues.complianceCategories,
        entityUuids,
        tanUuids,
        startDate: toApiDate(formValues.compliancePeriodDate),
      }

      const response = await createComplianceEntry(payload)
      if (response.success) {
        setModalOpen(false)
        setFormValues({ ...DEFAULT_FORM_VALUES })
        if (response?.data?.success) {
          if (response?.data?.meta?.message) {
            showSnackbar(response?.data?.meta?.message, 'success')
          } else {
            showSnackbar('Compliance created successfully', 'success')
          }
        } else if (response?.data?.errors?.length) {
          showSnackbar(response?.data?.errors[0]?.message, 'error')
        }
        // Refresh the table from the backend to reflect the new entries.
        await loadAllData(1, itemsPerPage, getValidCriteria(searchCriteria))
      } else {
        setFormErrors({
          general: response.error || 'Failed to create Compliance',
        })
      }
    } catch (error) {
      setFormErrors({
        general: error.message || 'An unexpected error occurred',
      })
    } finally {
      setFormLoading(false)
    }
  }

  // Selection handlers
  const handleSelectItem = itemId => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  const handleSelectAll = event => {
    if (event.target.checked) {
      setSelectedItems(entries.map(item => item.uuid || item.id))
    } else {
      setSelectedItems([])
    }
  }

  // Delete handlers
  const handleBulkDeleteClick = () => {
    setDeleteTargets([...selectedItems])
    setDeleteDialogOpen(true)
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setDeleteTargets([])
  }

  const handleConfirmDelete = async () => {
    try {
      const response = await deleteComplianceEntries(deleteTargets)
      if (response.success) {
        const targetSet = new Set(deleteTargets)
        setEntries(prev =>
          prev.filter(entry => !targetSet.has(entry.uuid || entry.id))
        )
        setTotalItems(prev => Math.max(0, prev - deleteTargets.length))
        setSelectedItems(prev => prev.filter(id => !targetSet.has(id)))
        setDeleteDialogOpen(false)
        setDeleteTargets([])
        showSnackbar(
          'Selected Compliance(s) are successfully deleted',
          'success'
        )
      } else {
        showSnackbar(response.error, 'error')
      }
    } catch (error) {
      showSnackbar(
        error.message || 'Failed to delete Compliance entries',
        'error'
      )
    }
  }

  // Pagination handlers
  const handlePageChange = page => {
    loadAllData(page, itemsPerPage, getValidCriteria(searchCriteria))
  }

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    loadAllData(1, newItemsPerPage, getValidCriteria(searchCriteria))
  }

  // Download handler
  const handleDownload = async () => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }
    try {
      const payload = {
        pageFilter: {
          currentPage: currentPage,
          includeCount: true,
          pageSize: DEFAULT_PAGE,
          sortField: 'createdDate',
          sortOrder: 'DESC',
        },
        requestFilter: [],
        isReportDownload: true,
        searchCOList: getValidCriteria(searchCriteria),
      }
      // The download endpoint queues a report and returns a JSON response with a
      // download id; the file itself is retrieved from the Download page.
      const response = await downloadComplianceExcel(payload)
      if (response?.success && response?.data) {
        saveBlobAsFile(response.data, 'assign_compliances.xlsx')
      } else {
        showSnackbar(response.error || 'Failed to download excel', 'error')
      }
    } catch (e) {
      showSnackbar(e.message || 'Failed to download excel', 'error')
    }
  }

  // Search handlers
  const handleSearch = event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async searchList => {
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    await loadAllData(1, itemsPerPage, searchList)
    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    await loadAllData(1, itemsPerPage, [])
    setSearchOpen(false)
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }

  return (
    <Box>
      {/* Sub Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '32px',
          py: '12px',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            color: 'var(--forbrand-secondary)',
            fontWeight: 500,
          }}
        >
          Assign Compliances
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            position: 'relative',
          }}
        >
          <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={GLOBAL_CONSTANTS.SEARCH}>
            <IconButton onClick={handleSearch}>
              <SearchIconBtn />
            </IconButton>
          </Tooltip>

          {selectedItems.length > 0 && roleConfig.canEdit && (
            <>
              <Typography
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--forbrand-color)',
                  marginRight: '6px',
                }}
              ></Typography>
              <Tooltip title="Delete">
                <IconButton onClick={handleBulkDeleteClick}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {roleConfig.canEdit && (
            <>
              <Typography
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--forbrand-color)',
                  marginRight: '6px',
                }}
              ></Typography>

              <Button
                variant="contained"
                onClick={handleAddClick}
                sx={{
                  backgroundColor: 'var( --forbrand-bg-variant)',
                  color: 'var(--forbrand-brand-varient)',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '16px',
                }}
              >
                Add
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Table */}
      <Box>
        <CommonTable
          columns={columns}
          data={entries}
          loading={loading}
          emptyMessage="No compliance entries found"
          showFooter={true}
          totalItems={totalItems}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
          showTotalItems={true}
          showPageNumbers={true}
          showActions={false}
          showCheckbox={roleConfig.canEdit}
          selectedItems={selectedItems}
          onSelectItem={handleSelectItem}
          onSelectAll={handleSelectAll}
        />
      </Box>

      {/* Add/Edit Compliance Modal */}
      <CommonModal
        open={modalOpen}
        onClose={handleModalClose}
        title={formMode === 'edit' ? 'Edit Compliance' : 'Add Compliance'}
        maxWidth="sm"
      >
        <ComplianceMasterForm
          mode={formMode}
          values={formValues}
          entities={entities}
          tanList={tanList}
          categories={categories}
          formErrors={formErrors}
          formLoading={formLoading}
          onFieldChange={handleFieldChange}
          onSubmit={handleFormSubmit}
        />
      </CommonModal>

      {/* Delete Confirmation Dialog */}
      <ComplianceDeleteDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        count={deleteTargets.length}
      />

      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={searchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />
    </Box>
  )
}

export default ComplianceMaster
